import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Self-contained job progress banner so its 1-second updates don't re-render the parent.
 * Computes elapsed from job.startTime and Date.now() on each tick.
 */
const ActiveJobsBanner = ({ activeJobs, isDark }) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (activeJobs.length === 0) return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [activeJobs.length]);

  if (activeJobs.length === 0) return null;

  return (
    <div className="mb-4 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 text-cyan-400 animate-spin" />
        <div className="flex-1">
          <div className="text-sm font-medium text-cyan-300">
            Processing {activeJobs.length} job{activeJobs.length !== 1 ? 's' : ''}...
          </div>
          <div className="text-xs text-zinc-400 mt-1 space-y-3">
            {activeJobs.map((job) => {
              const estimatedSeconds = job.estimatedTime || (job.videoCount || 0) * 90;
              const startTime = job.startTime || Date.now();
              const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
              const remainingSeconds = Math.max(0, estimatedSeconds - elapsedSeconds);
              const progress = estimatedSeconds > 0 ? Math.min(100, (elapsedSeconds / estimatedSeconds) * 100) : 0;
              const minutes = Math.floor(remainingSeconds / 60);
              const seconds = remainingSeconds % 60;
              const timeDisplay = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

              return (
                <div key={job.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-300">
                      Adding {job.videoCount || 0} video{job.videoCount !== 1 ? 's' : ''} to library
                    </span>
                    <span className="text-cyan-300 font-medium">
                      {timeDisplay} remaining
                    </span>
                  </div>
                  <div className="w-full bg-zinc-700 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-cyan-500 to-cyan-400 h-full transition-all duration-500 ease-out"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">
                      {Math.floor(elapsedSeconds / 60)}m {elapsedSeconds % 60}s elapsed
                    </span>
                    <span className="text-zinc-500">
                      {Math.round(progress)}% complete
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveJobsBanner;
